import { z, toJsonSchema } from './validation';
import { ConsentSchema, EventSchema } from './walkeros';
import {
  ValueSchema,
  ValuesSchema,
  RuleSchema,
  RulesSchema,
  PolicySchema,
} from './mapping';
import { Identifier } from './primitives';
import { RouteWithoutManySchema } from './matcher';
import { EventCacheSchema } from './cache';
import { StateSchema } from './state';
import { LoggerConfigSchema } from './logger';

/**
 * Destination Schemas
 *
 * Mirrors: types/destination.ts
 * Purpose: Runtime validation and JSON Schema generation for destination configurations
 *
 * Destinations are the endpoints where processed events are sent (analytics tools,
 * marketing platforms, data warehouses, etc.). This file defines schemas for:
 * - Destination configuration
 * - Type bundles for generic constraints
 * - Push contexts
 * - Batching structures
 */

// ========================================
// Configuration Schemas
// ========================================

/**
 * Config - Destination configuration
 *
 * Defines how a destination processes events:
 * - Consent requirements
 * - Settings (destination-specific)
 * - Data transformations
 * - Environment dependencies
 * - Initialization options
 * - Mapping rules
 * - Processing policies
 * - Queueing behavior
 * - Logging verbosity
 * - Error/log handlers
 *
 * Generic note: settings, env, and mapping can have destination-specific types
 * but for schema validation we use z.unknown() to allow flexibility
 */
export const ConfigSchema = z
  .object({
    consent: ConsentSchema.optional().describe(
      'Required consent states to send events to this destination',
    ),
    settings: z
      .any()
      .meta({
        id: 'DestinationSettings',
        title: 'Destination.Settings',
        description:
          'Implementation-specific configuration (destination-defined shape).',
      })
      .describe('Implementation-specific configuration')
      .optional(),
    credentials: z
      .any()
      .meta({
        id: 'DestinationCredentials',
        title: 'Destination.Credentials',
        description:
          'Optional credentials slot (destination-defined shape); supports $env. The per-package shape is supplied via mergeConfigSchema.',
      })
      .describe('Optional credentials (destination-defined shape)')
      .optional(),
    data: z
      .union([ValueSchema, ValuesSchema])
      .optional()
      .describe(
        'Global data transformation applied to all events for this destination',
      ),
    include: z
      .array(z.string())
      .optional()
      .describe('Event sections to flatten into context.data'),
    env: z
      .any()
      .meta({
        id: 'DestinationEnv',
        title: 'Destination.Env',
        description:
          'Environment dependencies (destination-defined shape) — platform, SDK, or mock hook.',
      })
      .describe('Environment dependencies (platform-specific)')
      .optional(),
    id: Identifier.describe(
      'Destination instance identifier (defaults to destination key)',
    ).optional(),
    init: z.boolean().describe('Whether to initialize immediately').optional(),
    loadScript: z
      .boolean()
      .describe('Whether to load external script (for web destinations)')
      .optional(),
    mapping: RulesSchema.optional().describe(
      'Entity-action specific mapping rules for this destination',
    ),
    policy: PolicySchema.optional().describe(
      'Pre-processing policy rules applied before event mapping',
    ),
    queue: z
      .boolean()
      .describe('Whether to queue events when consent is not granted')
      .optional(),
    require: z
      .array(z.string())
      .optional()
      .describe(
        'Defer destination initialization until these collector events fire (e.g., ["consent"])',
      ),
    logger: LoggerConfigSchema.optional().describe(
      'Logger configuration (level, handler) to override the collector defaults',
    ),
    setup: z
      .union([z.boolean(), z.record(z.string(), z.unknown())])
      .optional()
      .describe(
        'One-time setup options applied during destination registration (boolean enables defaults, object configures specifics)',
      ),
    before: RouteWithoutManySchema.optional().describe(
      'Post-collector transformer chain applied before this destination receives the event. `many` is not valid here — use multiple destinations for post-collector fan-out.',
    ),
    next: RouteWithoutManySchema.optional().describe(
      'Post-push transformer chain. Runs after destination push completes; push response is available at ingest._response. `many` is not valid here — use multiple destinations for post-collector fan-out.',
    ),
    cache: EventCacheSchema.optional().describe(
      'Cache configuration for deduplication; skip push on cache HIT',
    ),
    state: z
      .union([StateSchema, z.array(StateSchema)])
      .optional()
      .describe(
        'Declarative store get/set operations applied around this destination',
      ),
    disabled: z
      .boolean()
      .describe('Completely skip this destination (no init, no push, no queue)')
      .optional(),
    mock: z
      .unknown()
      .meta({
        id: 'DestinationMock',
        title: 'Destination.Mock',
        description:
          'Return this value instead of calling push(). Dev/testing only.',
      })
      .optional()
      .describe(
        'Return this value instead of calling push(). Dev/testing only.',
      ),
    queueMax: z
      .number()
      .optional()
      .describe(
        'Maximum consent-denied events retained in queuePush for this destination. FIFO drop on overflow. Default 1000.',
      ),
    dlqMax: z
      .number()
      .optional()
      .describe(
        'Maximum failed-push entries retained in dlq for this destination. FIFO drop on overflow. Default 100.',
      ),
    timeout: z
      .number()
      .optional()
      .describe(
        'Per-destination delivery timeout in ms (default 10000); a delivery that does not settle in this window is routed to the DLQ like a thrown push.',
      ),
    batch: z
      .union([
        z.number(),
        z.object({
          wait: z.number().optional(),
          size: z.number().optional(),
          age: z.number().optional(),
        }),
      ])
      .optional()
      .describe(
        "Enables batching for all of this destination's events into one shared default buffer; a mapping rule's own batch splits that entity-action into its own buffer and overrides per field. Bare number is the debounce wait window; object form supports wait (debounce ms), size (count cap, default 1000), age (max ms since first entry, default 30000).",
      ),
    breaker: z
      .union([
        z.number(),
        z.object({
          threshold: z.number().optional(),
          cooldown: z.number().optional(),
        }),
      ])
      .optional()
      .describe(
        'Per-destination circuit breaker (presence-gated). After threshold consecutive transport failures the breaker opens and events are skipped until cooldown ms elapse, then one probe is admitted; success closes it, failure re-opens it. Partial-batch row failures are breaker-neutral. Bare number is the threshold; object form supports threshold (default 5) and cooldown (default 30000ms).',
      ),
  })
  .meta({
    id: 'DestinationConfig',
    title: 'Destination.Config',
    description:
      'Destination configuration: consent, settings, data, env, mapping rules, policy, queue, logger, before/next chains, cache.',
  })
  .describe('Destination configuration');

/**
 * PartialConfig - Config with all fields optional
 * Used for config updates and overrides
 *
 * Note: All fields in ConfigSchema are already optional (.optional() on each field).
 * Using .partial() ensures the schema itself makes all fields optional at the type level.
 */
export const PartialConfigSchema = ConfigSchema.partial()
  .meta({
    id: 'DestinationPartialConfig',
    title: 'Destination.PartialConfig',
    description: 'Partial destination configuration with all fields optional.',
  })
  .describe('Partial destination configuration with all fields optional');

/**
 * Policy - Processing policy rules
 * Maps policy keys to transformation values
 * Applied before event mapping
 */
export const DestinationPolicySchema = PolicySchema.describe(
  'Destination policy rules for event pre-processing',
);
// Note: DestinationPolicySchema is an alias of MappingPolicySchema;
// it intentionally shares the Mapping.Policy id/title via the underlying schema.

// ========================================
// Context Schemas
// ========================================

/**
 * Context - Base destination context
 * Passed to init and push functions
 * Contains collector instance, config, data, and environment
 *
 * Note: collector is runtime instance, not easily serializable
 */
export const ContextSchema = z
  .object({
    collector: z
      .unknown()
      .meta({
        id: 'DestinationContextCollector',
        title: 'Collector.Instance',
        description:
          'Collector instance handed to the destination (runtime object).',
      })
      .describe('Collector instance (runtime object)'),
    config: ConfigSchema.describe('Destination configuration'),
    data: z
      .union([
        z.unknown(), // WalkerOS.Property
        z.array(z.unknown()),
      ])
      .optional()
      .describe('Transformed event data'),
    env: z
      .unknown()
      .meta({
        id: 'DestinationContextEnv',
        title: 'Destination.Env',
        description: 'Environment dependencies (destination-defined shape).',
      })
      .describe('Environment dependencies'),
  })
  .meta({
    id: 'DestinationContext',
    title: 'Destination.Context',
    description: 'Destination context passed to init and push functions.',
  })
  .describe('Destination context for init and push functions');

/**
 * PushContext - Context for push function
 * Extends Context with mapping rule information
 */
export const PushContextSchema = ContextSchema.extend({
  mapping: RuleSchema.optional().describe(
    'Resolved mapping rule for this specific event',
  ),
})
  .meta({
    id: 'DestinationPushContext',
    title: 'Destination.PushContext',
    description:
      'Destination context for push() with the resolved event mapping rule.',
  })
  .describe('Push context with event-specific mapping');

/**
 * PushBatchContext - Context for pushBatch function
 * Same as PushContext but for batch processing
 */
export const PushBatchContextSchema = PushContextSchema.describe(
  'Batch push context with event-specific mapping',
);
// Note: PushBatchContextSchema is the same shape as PushContextSchema; it
// shares the Destination.PushContext id via the underlying schema.

// ========================================
// Batch Processing Schemas
// ========================================

/**
 * PushEvent - Single event with mapping in a batch
 */
export const PushEventSchema = z
  .object({
    event: EventSchema.describe('The event to process'),
    mapping: RuleSchema.optional().describe('Mapping rule for this event'),
  })
  .meta({
    id: 'DestinationPushEvent',
    title: 'Destination.PushEvent',
    description:
      'Single event paired with its resolved mapping rule (batch element).',
  })
  .describe('Event with optional mapping for batch processing');

/**
 * PushEvents - Array of PushEvent
 */
export const PushEventsSchema = z
  .array(PushEventSchema)
  .meta({
    id: 'DestinationPushEvents',
    title: 'Destination.PushEvents',
    description: 'Array of events with mappings (batch input).',
  })
  .describe('Array of events with mappings');

/**
 * Batch - Batched events for processing
 * Groups events by mapping key for efficient batch sends
 */
export const BatchSchema = z
  .object({
    key: z
      .string()
      .describe('Batch key (usually mapping key like "product.view")'),
    events: z.array(EventSchema).describe('Array of events in batch'),
    data: z
      .array(
        z
          .union([
            z.unknown(), // WalkerOS.Property
            z.array(z.unknown()),
          ])
          .optional(),
      )
      .describe('Transformed data for each event'),
    mapping: RuleSchema.optional().describe('Shared mapping rule for batch'),
  })
  .meta({
    id: 'DestinationBatch',
    title: 'Destination.Batch',
    description: 'Batch of events grouped by mapping key for batch delivery.',
  })
  .describe('Batch of events grouped by mapping key');

/**
 * Data - Transformed event data types
 * Can be single property, undefined, or array of properties
 */
export const DataSchema = z
  .union([
    z.unknown(), // WalkerOS.Property
    z.array(z.unknown()),
  ])
  .meta({
    id: 'DestinationData',
    title: 'Destination.Data',
    description:
      'Transformed event data delivered to the destination (Property, undefined, or array).',
  })
  .optional()
  .describe('Transformed event data (Property, undefined, or array)');

// ========================================
// Instance Schemas
// ========================================

/**
 * Instance - Destination instance (runtime object)
 *
 * Note: This schema is primarily for documentation
 * Runtime functions (init, push, pushBatch) cannot be validated
 * Use z.unknown() for function fields
 */
export const InstanceSchema = z
  .object({
    config: ConfigSchema.describe('Destination configuration'),
    queue: z
      .array(EventSchema)
      .optional()
      .describe('Queued events awaiting consent'),
    dlq: z
      .array(z.tuple([EventSchema, z.unknown()]))
      .optional()
      .describe('Dead letter queue (failed events with errors)'),
    type: z.string().optional().describe('Destination type identifier'),
    env: z.unknown().optional().describe('Environment dependencies'),
    // Functions - not validated, use z.unknown()
    init: z.unknown().optional().describe('Initialization function'),
    push: z.unknown().describe('Push function for single events'),
    pushBatch: z.unknown().optional().describe('Batch push function'),
    on: z.unknown().optional().describe('Event lifecycle hook function'),
  })
  .meta({
    id: 'DestinationInstance',
    title: 'Destination.Instance',
    description:
      'Destination instance (runtime object with init/push/pushBatch/on).',
  })
  .describe('Destination instance (runtime object with functions)');

/**
 * Init - Initialization config
 * Contains destination code and configuration
 */
export const InitSchema = z
  .object({
    code: InstanceSchema.describe('Destination instance with implementation'),
    config: PartialConfigSchema.optional().describe(
      'Partial configuration overrides',
    ),
    env: z.unknown().optional().describe('Partial environment overrides'),
  })
  .meta({
    id: 'DestinationInit',
    title: 'Destination.Init',
    description:
      'Destination initialization bundle (instance code + config + env).',
  })
  .describe('Destination initialization configuration');

/**
 * InitDestinations - Map of destination IDs to Init configs
 */
export const InitDestinationsSchema = z
  .record(z.string(), InitSchema)
  .meta({
    id: 'DestinationInitDestinations',
    title: 'Destination.InitDestinations',
    description: 'Map of destination IDs to initialization configurations.',
  })
  .describe('Map of destination IDs to initialization configurations');

/**
 * Destinations - Map of destination IDs to instances
 */
export const DestinationsSchema = z
  .record(z.string(), InstanceSchema)
  .meta({
    id: 'DestinationDestinations',
    title: 'Destination.Destinations',
    description: 'Map of destination IDs to runtime destination instances.',
  })
  .describe('Map of destination IDs to runtime instances');

// ========================================
// Result Schemas
// ========================================

/**
 * Ref - Destination reference
 * Contains destination type and optional response data or error
 */
export const RefSchema = z
  .object({
    type: z.string().describe('Destination type ("gtag", "meta", "bigquery")'),
    data: z.unknown().optional().describe('Response from push()'),
    error: z.unknown().optional().describe('Error if failed'),
  })
  .meta({
    id: 'DestinationRef',
    title: 'Destination.Ref',
    description:
      'Reference to a destination outcome (type + response data or error).',
  })
  .describe('Destination reference with type and response data');

/**
 * Push - Push operation result
 */
export const PushResultSchema = z
  .object({
    queue: z
      .array(EventSchema)
      .optional()
      .describe('Events queued (awaiting consent)'),
    error: z.unknown().optional().describe('Error if push failed'),
  })
  .meta({
    id: 'DestinationPushResult',
    title: 'Destination.PushResult',
    description:
      'Result of a single destination push (queued events, optional error).',
  })
  .describe('Push operation result');

/**
 * Result - Overall processing result
 * Note: This type has been removed from types/destination.ts
 * Results are now part of Elb.PushResult with Record<string, Ref> structure
 */
export const ResultSchema = z
  .object({
    ok: z.boolean().describe('True if nothing failed'),
    event: z.unknown().optional().describe('The processed event'),
    done: z
      .record(z.string(), RefSchema)
      .optional()
      .describe('Destinations that processed successfully'),
    queued: z
      .record(z.string(), RefSchema)
      .optional()
      .describe('Destinations that queued events'),
    failed: z
      .record(z.string(), RefSchema)
      .optional()
      .describe('Destinations that failed to process'),
  })
  .meta({
    id: 'DestinationResult',
    title: 'Destination.Result',
    description:
      'Overall push result aggregated across all destinations (done/queued/failed maps).',
  })
  .describe('Push result with destination outcomes');

/**
 * DLQ - Dead Letter Queue
 * Array of failed events with their errors
 */
export const DLQSchema = z
  .array(z.tuple([EventSchema, z.unknown()]))
  .meta({
    id: 'DestinationDLQ',
    title: 'Destination.DLQ',
    description: 'Dead-letter queue: list of [event, error] tuples.',
  })
  .describe('Dead letter queue: [(event, error), ...]');

// ========================================
// JSON Schema Exports (for Explorer/RJSF/MCP)
// ========================================

export const configJsonSchema = toJsonSchema(ConfigSchema, 'DestinationConfig');

export const partialConfigJsonSchema = toJsonSchema(
  PartialConfigSchema,
  'PartialDestinationConfig',
);

export const contextJsonSchema = toJsonSchema(
  ContextSchema,
  'DestinationContext',
);

export const pushContextJsonSchema = toJsonSchema(
  PushContextSchema,
  'PushContext',
);

export const batchJsonSchema = toJsonSchema(BatchSchema, 'Batch');

export const instanceJsonSchema = toJsonSchema(
  InstanceSchema,
  'DestinationInstance',
);

export const resultJsonSchema = toJsonSchema(ResultSchema, 'DestinationResult');
